import {Storage} from "@/Services/Storage";
import axios from "axios";
import router from "@/router";

export default {
    name: 'settings',
    namespaced: true,
    state: {},
    getters: {
        /**
         * Current store DB name derived from root state.currentStore
         */
        currentStoreDb: (state, getters, rootState) => (rootState.currentStore && (rootState.currentStore.StoreDB || rootState.currentStore.db)) || '',
        /**
         * Current store Name derived from root state.currentStore (covers legacy field names)
         */
        currentStoreName: (state, getters, rootState) => {
            const cs = rootState.currentStore || {};
            return cs.StoreName || cs.name || cs.storeName || cs.Description || '';
        },
        /**
         * Whether the current store is SAP-registered/integrated.
         * Uses either explicit flag on currentStore or presence of SAP API key from integrations/sap.
         */
        /**
         * True if current store is SAP-registered OR an integration key is present.
         * Broadened to cover legacy flag names and truthy variants so the UI can show
         * the button even before the API key is fetched.
         */
        isCurrentStoreSapRegistered: (state, getters, rootState, rootGetters) => {
            const cs = rootState.currentStore || {};
            const truthy = v => v === true || v === 'true' || v === 1 || v === '1' || v === 'Y' || v === 'y';
            const keys = [
                'isSapRegistered', 'sapRegistered', 'IsSapRegistered',
                'sapIntegration', 'sapIntegrated', 'isSapIntegrated',
                'hasSap', 'HasSap', 'SAP', 'sap'
            ];
            const flag = keys.some(k => truthy(cs[k]));
            const hasKey = !!rootGetters['integrations/sap/isSapIntegrated'];
            return !!(flag || hasKey);
        },
    },
    mutations: {},
    actions: {
        getEmail: async () => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(franchiseTomcatApi+`emailrecord/${storeDB}/getEmailSettings`)
                .then(({data}) => data);
        },
        getStoreLogo: async (context) => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(stocklistApi2 + `settings/${storeDB}/getLogo`)
                .then(({data}) => data);
        },
        setStoreLogo: async (context, payload) => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.post(stocklistApi2 + `settings/${storeDB}/addLogo`, payload, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
                .catch((err) => errorHandler.tomcatError(err, false, `Could not Set Logo`));
        },
        getOnlineSettings: async () => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(onlineSalesTomcatApi + `settings/${storeDB}/Online`)
                .then(({data}) => data).catch(err => errorHandler.tomcatError(err));
        },
        /**
         * Save basic store settings via FranchiseSpringServer.
         * Endpoint: POST `${stocklistApi2}settings/${DB}/saveBasicSettings`
         * Implements enforcement: onlineInvoicing is always true; laybyDuration coerced to integer with default 7.
         * Excludes nullable printLogo from the payload.
         *
         * @param {Object} context
         * @param {Object} payload BasicSettings request body
         * @returns {Promise<void>} Resolves after save and state refresh/login
         */
        saveBasicSettings: async (context, payload) => {
            let storeDB = await Storage.getCurrentStoreDB();
            let userDetails = await Storage.getMobileUser();

            // Build defensive body according to API schema
            const body = { ...payload };
            // Enforce onlineInvoicing always true per requirements
            body.onlineInvoicing = true;
            // Coerce laybyDuration to a positive integer, default 7
            if (body.laybyDuration == null || !Number.isFinite(+body.laybyDuration) || +body.laybyDuration <= 0) {
                body.laybyDuration = 7;
            } else {
                body.laybyDuration = Math.trunc(+body.laybyDuration);
            }
            // Ensure booleans are strict booleans
            if (typeof body.priceMakesPrice !== 'boolean') body.priceMakesPrice = !!body.priceMakesPrice;
            if (typeof body.hasVat !== 'boolean') body.hasVat = !!body.hasVat;
            if (typeof body.allowBelowZero !== 'boolean') body.allowBelowZero = !!body.allowBelowZero;
            if (typeof body.useSeperateLine !== 'boolean') body.useSeperateLine = !!body.useSeperateLine;
            if (typeof body.gateEntry !== 'boolean') body.gateEntry = !!body.gateEntry;
            if (typeof body.checkDigit !== 'boolean') body.checkDigit = !!body.checkDigit;
            // Do not send printLogo (nullable and not part of current UI)
            if ('printLogo' in body) delete body.printLogo;

            return axios.post(stocklistApi2 + `settings/${storeDB}/saveBasicSettings`, body)
                .then(() => {
                    return Promise.all([
                        context.dispatch(`refreshState`),
                        context.dispatch(`mobileUserLogin`, userDetails)
                    ])
                })
                .catch((err) => errorHandler.tomcatError(err, false, `Could not Save Settings`));
        },
        addStoreToCustomer: async (context, payload) => {
            // Extract query parameters from the payload
            const { queryParams, ...bodyPayload } = payload;
            const mainCustomerID = queryParams?.mainCustomerID;

            // console.log("MAIN CUSTOM :::", mainCustomerID)
            return axios
                .post(
                    subscriptionApi + `customerUpdate/addStore/${mainCustomerID}`, bodyPayload)
                .then(({ data }) => {
                    context.dispatch("SystemSettings", queryParams.mainCustomerID);
                    return data;
                })
                .catch((err) => errorHandler.tomcatError(err));
        },
        labelApi2: async () => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(DeliveryNote + `labels/${storeDB}/getLabelSetup`)
                .then(({data}) => data).catch(err => errorHandler.tomcatError(err))
        },
        labelApi: async () => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(DeliveryNote + `labels/${storeDB}/getAllLabelFields`)
                .then(({ data }) => {
                    if (data?.message === "LabelFields not found") {
                        return null; // Return null or any default value you prefer
                    }
                    return data;
                })
                .catch(err => {
                    if (err.response?.data?.message === "LabelFields not found") {
                        return null; // Don't call errorHandler, just return a safe value
                    }
                    return errorHandler.tomcatError(err); // Handle other errors normally
                });
        },



        saveEmailSetup: async (context, payload) => {
            let storeDB = await Storage.getCurrentStoreDB();
             return axios.post(franchiseTomcatApi+`emailrecord/${storeDB}/setEmailSettings`, payload)
                .catch((err) => errorHandler.tomcatError(err, false, `Could not Save Email settings`));
        },
        requestPasswordReset: async (context, username) => {
            return axios.post(franchiseTomcatApi + `SetKazangUser/${username}/forgotPasswordRequest`, {
                username: username
            }).catch(err => errorHandler.tomcatError(err))
        },

        resetPassword: async (context, payload) => {
            return axios.post(franchiseTomcatApi + `SetKazangUser/${payload.username}/resetPassword`, payload)
                .then(() => router.push({name: 'login'}))
                .catch(err => errorHandler.tomcatError(err))
        },
        auditVAT: async (context, range) => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(onlineSalesTomcatApi + `invoice/${storeDB}/auditVATInvoices?from=${range.from}&to=${range.to}`)
                .catch(err => errorHandler.tomcatError(err))
        },

        /**
         * Get store registration details
         * @param {Object} context - Vuex context
         * @returns {Promise<Object>} Store registration details
         */
        getStoreRegistrationDetails: async (context) => {
            const storeDB = await Storage.getCurrentStoreDB();
            return axios.get(franchiseTomcatApi + `stores/getRegistrationDetails?database=${storeDB}`)
                .then(({data}) => data)
                .catch((err) => errorHandler.tomcatError(err));
        },

        /**
         * Check ZRA details to determine if the store has VAT
         * @param {Object} context - Vuex context
         * @returns {Promise<boolean>} True if the store has VAT, false otherwise
         */
        checkZRADetails: async (context) => {
            try {
                let storeTpin = await context.dispatch('getOnlineSettings1').then((res) => {
                    return res.vatNumber;
                });

                await context.dispatch('getCustomerDetails').then((store) => {
                    storeTpin = (storeTpin ? storeTpin : store.Addr3.replace(/\D/g, ""));
                });

                const { data } = await axios.get(`${franchiseTomcatApi}AndroidLogin/TPIN/${storeTpin}`);

                if (data && data.taxTypes && data !== "Invalid TPIN") {
                    return data.taxTypes.toLowerCase().includes("value added tax");
                }
                return false;
            } catch (error) {
                console.log(error, "here is the error");
                return false;
            }
        },

        /**
         * Check if smart invoicing is enabled for the store
         * @param {Object} context - Vuex context
         * @returns {Promise<boolean>} True if smart invoicing is enabled, false otherwise
         */
        getSmartInvoiceStatus: async (context) => {
            let storeDB = await Storage.getCurrentStoreDB();
            if (!storeDB) return false;

            return axios
                .get(franchiseTomcatApi + `GRV/${storeDB}/checkSmartInvoicing`)
                .then(({ data }) => data)
                .catch((err) => {
                    errorHandler.tomcatError(err);
                    return false;
                });
        }
    }
}
